/**
 * Add admin modal — per docs/specs/admin-audit.md §6.
 *
 * Email search against existing users → assign role. Triggers welcome email
 * with 2FA setup link. Owner-only.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { addAdminUser } from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';
import type { AdminRole } from '../../lib/types';

export function AddAdminModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<AdminRole>('support');
    const [saving, setSaving] = useState(false);

    const qc = useQueryClient();
    const { toast } = useToast();

    const handleClose = () => {
        if (saving) return;
        setEmail('');
        setRole('support');
        onClose();
    };

    const handleAdd = async () => {
        const trimmed = email.trim();
        if (!trimmed.includes('@')) return;
        setSaving(true);
        try {
            await addAdminUser(trimmed, role);
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'admins'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'kpis'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'audit'] })
            ]);
            toast('Admin access granted', { detail: `${trimmed} · ${role}` });
            setEmail('');
            setRole('support');
            onClose();
        } catch (error) {
            toast('Failed to add admin', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Admin access could not be granted')
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Add admin"
            subtitle="Grants admin access. Triggers welcome email with 2FA setup link."
            width={520}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleClose}>
                        Cancel
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={handleAdd}
                        disabled={!email.trim().includes('@') || saving}
                    >
                        {saving ? 'Adding…' : 'Add admin'}
                    </RoleButton>
                </>
            }
        >
            <div className="form-field">
                <label className="form-label" htmlFor="admin-email">
                    User email
                </label>
                <input
                    id="admin-email"
                    className="form-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="existing.user@example.com"
                />
                <div className="form-hint">
                    Must match an existing PD user account. Admin access attaches to the existing
                    user record and the selected role is stored in Supabase auth metadata.
                </div>
            </div>

            <div className="form-field">
                <span className="form-label">Role</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <RoleOption
                        id="owner"
                        role={role}
                        setRole={setRole}
                        title="Owner"
                        description="Everything — destructive actions, policy changes, plan overrides."
                    />
                    <RoleOption
                        id="support"
                        role={role}
                        setRole={setRole}
                        title="Support"
                        description="User management, tickets, billing health. No destructive ops."
                    />
                    <RoleOption
                        id="read_only"
                        role={role}
                        setRole={setRole}
                        title="Read-only"
                        description="View-only across every page. No action buttons."
                    />
                </div>
            </div>

            <p
                style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    margin: '10px 0 0',
                    padding: 10,
                    background: 'var(--warning-surface)',
                    borderRadius: 6,
                    border: '1px solid color-mix(in oklab, var(--warning) 30%, transparent)'
                }}
            >
                Admins must enable 2FA within 7 days or auto-demote to read-only.
            </p>
        </Modal>
    );
}

function RoleOption({
    id,
    role,
    setRole,
    title,
    description
}: {
    id: AdminRole;
    role: AdminRole;
    setRole: (r: AdminRole) => void;
    title: string;
    description: string;
}) {
    const checked = role === id;
    return (
        <label
            style={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr',
                gap: 10,
                padding: '10px 12px',
                border: `1px solid ${checked ? 'var(--border-focus)' : 'var(--border)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                background: checked ? 'var(--primary-surface)' : 'transparent'
            }}
        >
            <input
                type="radio"
                name="role"
                checked={checked}
                onChange={() => setRole(id)}
                style={{ marginTop: 2 }}
            />
            <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {description}
                </div>
            </div>
        </label>
    );
}
